import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';
import { UserDeck } from '@/types/deck';

interface CompactDeckCardProps {
  readonly userDeck: UserDeck;
  readonly onView: () => void;
  readonly onSetDefault: () => void;
}

export function CompactDeckCard({ 
  userDeck, 
  onView, 
  onSetDefault 
}: Readonly<CompactDeckCardProps>) {
  // Check if this is the system default deck
  const isSystemDefault = userDeck.deck.id === -1;
  
  // Randomly select a king for preview
  const kings = ["A13", "B13", "C13", "D13"];
  const randomKing = kings[Math.floor(Math.random() * kings.length)];
  
  return (
    <div 
      className="overflow-hidden rounded-lg bg-black/80 hover:bg-black/90 transition-all cursor-pointer h-full flex flex-col w-full text-left"
      onClick={onView}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onView();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Card Preview */}
      <div className="relative bg-gradient-to-b from-gray-900/60 to-black/80 flex-grow">
        
        
        {userDeck.isDefault && (
          <div className="absolute top-2 left-2 z-10">
            <Badge className="bg-yellow-600/80 text-white text-xs rounded-sm font-medium flex items-center gap-1">
              <Star className="h-3 w-3 fill-yellow-100" />
              <span>Default</span>
            </Badge>
          </div>
        )}

        {userDeck.deck.managerMode && (
          <div className="absolute top-2 right-2 z-10">
            <Badge className="bg-purple-600/40 text-purple-300 text-xs rounded-sm font-medium">
              Manager
            </Badge>
          </div>
        )}
        
        <div className="aspect-square w-full relative flex justify-center items-center">
          {/* Back card */}
          <div className="absolute transform translate-x-3">
            <img
              src={`${userDeck.deck.deckUrl}/back.svg`}
              alt="Card Back"
              className="w-32 h-auto object-contain"
            />
          </div>
          {/* King card */}
          <div className="absolute transform -translate-x-3">
            <img
              src={`${userDeck.deck.deckUrl}/${randomKing}.svg`}
              alt={userDeck.deck.name}
              className="w-32 h-auto object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = `${userDeck.deck.deckUrl}/back.svg`;
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Deck Info */}
      <div className="p-3 pt-2 bg-black">
        <h3 className="font-bold text-base text-white line-clamp-1">{userDeck.deck.name}</h3>
        <div className="flex justify-between items-center mt-1">
          <Badge variant="outline" className="bg-black/20 text-xs py-0 px-2 h-5 capitalize border border-gray-800">
            {userDeck.deck.deckType ?? 'standard'}
          </Badge>
          
          {!isSystemDefault && !userDeck.isDefault && (
            <Button 
              size="sm" 
              variant="ghost"
              className="h-6 px-2 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-950/50"
              onClick={(e) => {
                e.stopPropagation();
                onSetDefault();
              }}
            >
              Set Default
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}